import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Bed, UtensilsCrossed, ShoppingBag } from 'lucide-react';

const OrderTypeDialog = ({
  open,
  onOpenChange,
  orderType,
  setOrderType,
  selectedTable,
  setSelectedTable,
  selectedBooking,
  setSelectedBooking,
  customerName,
  setCustomerName,
  tables,
  tableOrders,
  bookings
}) => {
  const getOrderTypeIcon = (type) => {
    switch(type) {
      case 'room_service': return <Bed className="h-4 w-4" />;
      case 'dine_in': return <UtensilsCrossed className="h-4 w-4" />;
      case 'takeaway': return <ShoppingBag className="h-4 w-4" />;
      default: return <UtensilsCrossed className="h-4 w-4" />;
    }
  };

  const getOrderTypeLabel = (type) => {
    switch(type) {
      case 'room_service': return 'Room Service';
      case 'dine_in': return 'Dine In';
      case 'takeaway': return 'Takeaway';
      default: return 'Dine In';
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Select Order Type</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-3 gap-3">
            {['dine_in', 'room_service', 'takeaway'].map(type => (
              <Card
                key={type}
                className={`cursor-pointer transition-all ${orderType === type ? 'ring-2 ring-primary' : 'hover:bg-accent'}`}
                onClick={() => {
                  setOrderType(type);
                  if (type !== 'dine_in') setSelectedTable(null);
                  if (type !== 'room_service') setSelectedBooking(null);
                }}
                data-testid={`order-type-${type}`}
              >
                <CardContent className="p-4 text-center">
                  <div className="h-10 w-10 mx-auto mb-2 rounded-full bg-primary/10 flex items-center justify-center">
                    {getOrderTypeIcon(type)}
                  </div>
                  <p className="font-medium text-sm">{getOrderTypeLabel(type)}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          {orderType === 'dine_in' && (
            <div className="space-y-2">
              <Label>Select Table</Label>
              <Select value={selectedTable?.id || ''} onValueChange={(v) => {
                const table = tables.find(t => t.id === v);
                setSelectedTable(table);
                setCustomerName(table ? 'Table ' + table.table_number : 'Walking Customer');
              }}>
                <SelectTrigger><SelectValue placeholder="Select table" /></SelectTrigger>
                <SelectContent>
                  {tables.map(table => (
                    <SelectItem key={table.id} value={table.id}>
                      Table {table.table_number} ({table.capacity} seats) 
                      {table.status === 'occupied' && tableOrders[table.id] && ` - ${tableOrders[table.id].length} orders`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {orderType === 'room_service' && (
            <div className="space-y-2">
              <Label>Select Room</Label>
              <Select value={selectedBooking?.id || ''} onValueChange={(v) => {
                const booking = bookings.find(b => b.id === v);
                setSelectedBooking(booking);
                setCustomerName(booking?.guest_name || 'Room Guest');
              }}>
                <SelectTrigger><SelectValue placeholder="Select room" /></SelectTrigger>
                <SelectContent>
                  {bookings.map(booking => (
                    <SelectItem key={booking.id} value={booking.id}>
                      Room {booking.room_number || booking.room_id?.slice(-4)} - {booking.guest_name || 'Guest'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="space-y-2">
            <Label>Customer Name</Label>
            <Input value={customerName} onChange={(e) => setCustomerName(e.target.value)} />
          </div>

          <Button className="w-full" onClick={() => onOpenChange(false)}>Confirm</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default OrderTypeDialog;
